import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Box, CircularProgress, Typography, IconButton, Button } from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  OpenInNew as OpenIcon
} from '@mui/icons-material';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import './PdfDisplay.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface PdfDisplayProps {
  url: string;
  title?: string;
}

const PdfDisplay: React.FC<PdfDisplayProps> = ({ url, title }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: any) => {
    console.error('PDF load error:', error);
    setError('Erreur lors du chargement du PDF');
    setLoading(false);
  };

  const goToPrevPage = () => {
    setPageNumber(Math.max(1, pageNumber - 1));
  };

  const goToNextPage = () => {
    setPageNumber(Math.min(numPages || 1, pageNumber + 1));
  };

  const zoomIn = () => {
    setScale(Math.min(scale + 0.2, 2.0));
  };

  const zoomOut = () => {
    setScale(Math.max(scale - 0.2, 0.5));
  };

  return (
    <Box>
      {/* Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" onClick={goToPrevPage} disabled={pageNumber <= 1}>
            <PrevIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption">
            Page {pageNumber} sur {numPages || '?'}
          </Typography>
          <IconButton size="small" onClick={goToNextPage} disabled={pageNumber >= (numPages || 1)}>
            <NextIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" onClick={zoomOut} disabled={scale <= 0.5}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption">{Math.round(scale * 100)}%</Typography>
          <IconButton size="small" onClick={zoomIn} disabled={scale >= 2.0}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <Button
            size="small"
            startIcon={<OpenIcon />}
            onClick={() => window.open(url, '_blank')}
          >
            Ouvrir
          </Button>
        </Box>
      </Box>

      {/* PDF Viewer */}
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: '600px',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}
      >
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box display="flex" flexDirection="column" alignItems="center" p={4}>
            <Typography color="error" gutterBottom>{error}</Typography>
          </Box>
        )}

        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
          error=""
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </Box>
    </Box>
  );
};

export default PdfDisplay;